// Single source of truth for the in-app changelog + "What's new" modal.
// Hand-maintained, NEWEST FIRST. Add a new entry at the top of CHANGELOG for
// each release. Set `major: true` for releases worth interrupting the user with
// — the "What's new" modal auto-opens once when the newest release is major and
// the current user hasn't seen it yet (see components/whats-new.tsx).
//
// `icon` strings map to a small curated lucide set in lib/changelog-icons.ts.

export interface ChangelogHighlight {
  /** lucide icon name — see CHANGELOG_ICONS in lib/changelog-icons.ts */
  icon: string;
  text: string;
}

export interface ChangelogRelease {
  /** Display version, e.g. "2.4.0". Also the per-user "last seen" key. */
  version: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  /** Short release title. */
  title: string;
  /** When true, the "What's new" modal pops for users who haven't seen it. */
  major: boolean;
  /** Optional icon callouts shown at the top of the entry / modal. */
  highlights?: ChangelogHighlight[];
  /** Plain bullet list of changes. */
  changes: string[];
}

export const CHANGELOG: ChangelogRelease[] = [
  {
    version: "2.4.0",
    date: "2026-06-14",
    title: "Mobile polish, universal lightbox & What's New",
    major: true,
    highlights: [
      { icon: "image", text: "Rebuilt photo lightbox — instant open, blur-up while loading, tap to hide captions" },
      { icon: "smartphone", text: "Mobile pass: bottom-sheet dialogs, instant sidebar, no more zoom-on-tap" },
      { icon: "sparkles", text: "This “What's new” panel, plus a full changelog page" },
    ],
    changes: [
      "Lightbox: a pulsing blurred preview shows while the photo loads and clears once it's sharp; tap the image to hide the text and see the full photo.",
      "Lightbox: quick fade on desktop, slide on mobile; on iPhone the Save and Share buttons are combined into one Share button.",
      "Reports list: the “+N” tile now reveals the remaining thumbnails inline instead of opening the lightbox.",
      "Reports: the summary and attention items sit directly in the expanded row (no nested card).",
      "Report actions menu flattened so submenus no longer get cut off on mobile.",
      "Calendar shows a small “V” on VRBO reservations.",
      "Dialogs are now a scrollable bottom sheet on phones and a centered dialog on desktop; fixed the cleaning-item editor scrolling sideways.",
      "Text fields no longer zoom the page when focused on iOS.",
      "Sidebar opens instantly on mobile and covers the full screen height.",
      "Shorter cleaning-report SMS links (no “https://”, shorter code) and the daily digest is now an editable template.",
      "Property icon picker moved directly under the name; the dashboard “+ Reservation” button opens the form in place.",
    ],
  },
  {
    version: "2.3.0",
    date: "2026-06-13",
    title: "Lightbox save/share & reliable photo times",
    major: false,
    changes: [
      "Save and Share buttons in the photo lightbox (Save to Photos on iOS).",
      "A photo's capture time is always recorded now, even when the camera leaves it out.",
      "Report pages load much faster with smaller thumbnail images.",
      "Pulsing placeholders while thumbnails load.",
    ],
  },
  {
    version: "2.2.0",
    date: "2026-06-12",
    title: "Sign-in stability & cleaner-flow polish",
    major: false,
    changes: [
      "Fixed unexpected sign-outs (session refresh hardening).",
      "Cleaner camera: cleaner borders and clearer before/after labeling.",
      "Reworked the report header actions and share controls.",
    ],
  },
  {
    version: "2.1.0",
    date: "2026-06-09",
    title: "Reports redesign & AI summaries",
    major: true,
    highlights: [
      { icon: "layout", text: "Glanceable report detail: a verdict at the top, then the issues that need attention" },
      { icon: "sparkles", text: "Structured AI summaries with per-issue photo drill-down" },
    ],
    changes: [
      "New single-column report layout: status verdict, attention items with their photos, and a collapsible “all photos” section.",
      "Expandable rows on the reports list for quick triage without opening each report.",
      "AI summaries are now formatted with a prioritized list of attention items.",
    ],
  },
  {
    version: "2.0.0",
    date: "2026-06-06",
    title: "Unified cleaner + admin experience",
    major: true,
    highlights: [
      { icon: "sparkles", text: "A faster, cleaner admin shell with a sidebar" },
    ],
    changes: [
      "Consolidated the cleaner and admin flows into one consistent experience.",
      "Searchable icon picker hardened against crashes.",
      "More robust AI photo grading (schema validation + automatic retries).",
      "Eastern time everywhere, plus assorted auth and timezone fixes.",
    ],
  },
  {
    version: "1.5.0",
    date: "2026-06-01",
    title: "VRBO calendars & faster uploads",
    major: true,
    highlights: [
      { icon: "calendar", text: "VRBO iCal feeds alongside Airbnb" },
    ],
    changes: [
      "Per-property VRBO calendar support.",
      "Photos upload straight to storage — larger files and far fewer failed uploads.",
      "Faster report submission, HEIC photo handling, and bilingual fixes.",
    ],
  },
  {
    version: "1.4.0",
    date: "2026-05-30",
    title: "Smarter SMS & feed-health alerts",
    major: false,
    changes: [
      "Separate, clearer wording for cancellation texts.",
      "Admin alerts when an iCal feed can't be reached, with automatic retries.",
    ],
  },
  {
    version: "1.3.0",
    date: "2026-05-28",
    title: "Cleaning reminder fixes",
    major: false,
    changes: [
      "Corrected the dates shown in cleaning-reminder texts.",
    ],
  },
  {
    version: "1.0.0",
    date: "2026-04-23",
    title: "Initial release",
    major: true,
    highlights: [
      { icon: "calendar", text: "Multi-property reservation calendar from iCal feeds" },
      { icon: "message", text: "Automatic SMS to cleaners on new bookings" },
    ],
    changes: [
      "Multi-property reservation calendar parsed from Airbnb iCal feeds.",
      "Automatic SMS notifications with reply-to-confirm cleaning codes.",
      "Cleaning confirmation system, plus settings for properties and contacts.",
    ],
  },
];

/** The newest release — drives the "What's new" modal. */
export const LATEST_RELEASE = CHANGELOG[0];
